import { FC, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import {
  Box, Text, Flex, Spinner, IconButton, Button,
  Modal, ModalOverlay, ModalContent, useDisclosure,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { FaPlay, FaPause, FaExpand } from 'react-icons/fa';
import http from 'src/services/http';
import TemplatePreview, { TemplatePreviewHandle } from 'src/components/templates/TemplatePreview';
import useSmoothProgress from 'src/components/campaigns/creatives/utils/useSmoothProgress';
import useCopilotChatContext from '../hooks/useCopilotChatContext';
import FusionLoading from 'src/components/common/FusionLoading';

const PlayIcon = FaPlay as any;
const PauseIcon = FaPause as any;
const ExpandIcon = FaExpand as any;

interface VideoAdGeneratingProps {
  videoAdId: string;
}

const pulseAnimation = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

const CloseIcon: FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Module-level Set to track which videoAdIds have already notified the chat of completion.
// Survives component re-mounts (which happen when the chat re-renders due to new messages).
const notifiedVideoAdIds = new Set<string>();

// Backend status order — used to decide how far the stepper can advance.
// Each step belongs to a "group" (one of these statuses); the stepper can advance
// to the LAST step of the current group while waiting, then unlocks the next group
// when the backend transitions.
const STATUS_ORDER = [
  'pending',
  'generating_content',
  'generating_video',
  'extending_video',
  'completed',
] as const;
type StatusGroup = typeof STATUS_ORDER[number];

interface StepDef {
  id: number;
  label: string;
  group: StatusGroup;
  // Delay (ms) after the previous step completes before this step itself completes.
  // Tuned so the cumulative time roughly matches Veo's actual ~140s pipeline.
  delayMs: number;
}

const STEPS: StepDef[] = [
  { id: 1,  label: 'Analyzing your product',           group: 'pending',            delayMs: 0     },
  { id: 2,  label: 'Defining the brand mood',          group: 'generating_content', delayMs: 4000  },
  { id: 3,  label: 'Writing the ad copy',              group: 'generating_content', delayMs: 6000  },
  { id: 4,  label: 'Choosing the right voice',         group: 'generating_video',   delayMs: 8000  },
  { id: 5,  label: 'Casting the on-camera talent',     group: 'generating_video',   delayMs: 10000 },
  { id: 6,  label: 'Composing the opening shot',       group: 'generating_video',   delayMs: 12000 },
  { id: 7,  label: 'Setting up cinematography',        group: 'generating_video',   delayMs: 14000 },
  { id: 8,  label: 'Rendering main scene',             group: 'generating_video',   delayMs: 18000 },
  { id: 9,  label: 'Continuing the story',             group: 'extending_video',    delayMs: 18000 },
  { id: 10, label: 'Recording the call to action',     group: 'extending_video',    delayMs: 16000 },
  { id: 11, label: 'Color grading the final cut',      group: 'extending_video',    delayMs: 14000 },
  { id: 12, label: 'Almost ready...',                  group: 'extending_video',    delayMs: 10000 },
];

const CheckMark: FC = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
    <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function normalizeTemplate(template: any): any {
  const clone = JSON.parse(JSON.stringify(template));
  delete clone.output_format;
  if (!clone.duration && clone.elements?.[0]?.duration) {
    clone.duration = clone.elements[0].duration;
  }
  if (Array.isArray(clone.elements)) {
    clone.elements = clone.elements.map((el: any) => {
      if (typeof el.x === 'string') el.x = el.x.trim();
      if (typeof el.y === 'string') el.y = el.y.trim();
      if (typeof el.width === 'string') el.width = el.width.trim();
      if (typeof el.height === 'string') el.height = el.height.trim();
      if (el.elements && Array.isArray(el.elements)) {
        el.elements = el.elements.map((subEl: any) => {
          if (typeof subEl.x === 'string') subEl.x = subEl.x.trim();
          if (typeof subEl.y === 'string') subEl.y = subEl.y.trim();
          if (typeof subEl.width === 'string') subEl.width = subEl.width.trim();
          if (typeof subEl.height === 'string') subEl.height = subEl.height.trim();
          return subEl;
        });
      }
      return el;
    });
  }
  return clone;
}

/**
 * Exactly the same pattern as VideoSquareCreative in campaigns,
 * but with allowInteraction={true} on TemplatePreview so the SDK can receive events.
 */
const VideoAdGenerating: FC<VideoAdGeneratingProps> = ({ videoAdId }) => {
  const [status, setStatus] = useState<string>('pending');
  const [creatomateTemplate, setCreatomateTemplate] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  // True once the first poll has returned — used to avoid flashing the 12-step stepper
  // during the initial render (e.g. on page refresh, before we know the real status).
  const [firstFetchDone, setFirstFetchDone] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { isOpen: isZoomOpen, onOpen: openZoom, onClose: closeZoom } = useDisclosure();
  const { sendMessage, onboardingState, openCopilot, createFusionConversation, sendAutoMessage } = useCopilotChatContext();
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    try {
      openCopilot();
      await createFusionConversation();
      // Wait for React to commit the new conversation's state (messages, conversationId,
      // onboardingState) before sending the auto-message. Without this delay, sendAutoMessage
      // can race against the pending state flush — causing duplicate messages (the old
      // conversation's messages bleed into the new conversation's optimistic update) or
      // the message being routed to the wrong conversationId.
      await new Promise((resolve) => setTimeout(resolve, 500));
      if (sendAutoMessage) {
        await sendAutoMessage('Create Video Ad');
      }
    } catch {
      // Fallback: if a new conversation can't be created, just send the message here.
      sendMessage('Create Video Ad');
    } finally {
      setIsRetrying(false);
    }
  }, [openCopilot, createFusionConversation, sendAutoMessage, sendMessage]);

  // Video playback — same state as VideoSquareCreative
  const previewRef = useRef<TemplatePreviewHandle>(null);
  const [previewReady, setPreviewReady] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [duration, setDuration] = useState(8);
  const seekBarRef = useRef<HTMLDivElement>(null);
  const { progressBarRef, timeDisplayRef, onTimeChange, setTimeImmediate } = useSmoothProgress({ duration, isPlaying });

  useEffect(() => {
    if (!videoAdId) return;
    const pollVideoAd = async () => {
      try {
        const { data } = await http.get(`/v2/video-ads/${videoAdId}`);
        setStatus(data.status);
        setFirstFetchDone(true);
        if (data.status === 'completed' && data.creatomateTemplate) {
          setCreatomateTemplate(data.creatomateTemplate);
          if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
          // Notify the chat that the video is done so the backend can show the campaign offer.
          //
          // Guard against duplicate notifications in three layers:
          //  1) Module-level Set — blocks repeats from the same session (component re-mounts
          //     every time the chat re-renders; without this the poll would fire once per mount).
          //  2) Backend-state gate — the conversation's currentStep is the source of truth. If
          //     the backend already moved past 'create_video_ad_generating' (e.g. the user saw
          //     the offer in a previous session and now is refreshing the chat), do NOT re-send.
          //     Without this guard, a page refresh on a completed video ad re-sends the signal,
          //     producing ghost "VIDEO_AD_COMPLETE" user bubbles every time the chat reloads.
          //  3) hidden=true on sendMessage so the literal doesn't appear as a bubble in the
          //     session where we DO send it. (Markdown renders "__X__" as bold "X", which is
          //     why when the hidden flag is lost during history rehydration the label shows
          //     as "VIDEO_AD_COMPLETE" without underscores.)
          //
          // Wait 3s so the user has time to see the video before the campaign offer appears.
          const stepIsStillGenerating = onboardingState?.currentStep === 'create_video_ad_generating';
          if (!notifiedVideoAdIds.has(videoAdId) && stepIsStillGenerating) {
            notifiedVideoAdIds.add(videoAdId);
            setTimeout(() => {
              sendMessage('__VIDEO_AD_COMPLETE__', undefined, true);
            }, 3000);
          }
        } else if (data.status === 'failed') {
          setError(data.error || 'Video generation failed');
          if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
        }
      } catch (err) { console.error('[VideoAdGenerating] Poll error:', err); }
    };
    pollVideoAd();
    pollingRef.current = setInterval(pollVideoAd, 5000);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [videoAdId, sendMessage, onboardingState?.currentStep]);

  const normalizedTemplate = useMemo(() => {
    if (!creatomateTemplate) return null;
    // Pass template as-is from DB — same as campaign does with creative.attributes
    return JSON.parse(JSON.stringify(creatomateTemplate));
  }, [creatomateTemplate]);

  // Trust the template's composition duration over the SDK's getDuration(), which often returns
  // only the embedded video element's duration (8s) instead of the full composition (17s).
  const templateDuration = useMemo(() => {
    if (!creatomateTemplate) return 0;
    return creatomateTemplate.duration || creatomateTemplate.elements?.[0]?.duration || 0;
  }, [creatomateTemplate]);

  useEffect(() => {
    if (templateDuration > 0) {
      setDuration(templateDuration);
    }
  }, [templateDuration]);

  // Use the previewRef directly (scoped to THIS component) instead of the global debugger.
  // The global debugger's "last entry" can point to the zoom modal's preview after it's
  // been opened/closed, which breaks the embedded preview's controls.
  const onPreviewReady = useCallback(() => {
    setPreviewReady(true);
    setTimeout(() => {
      const preview = previewRef.current;
      if (preview) {
        preview.setControls(false);
        preview.setLoop(false);
        preview.setTime(5);
        // Prefer template duration; fall back to SDK, then to 8s.
        setDuration(templateDuration || preview.getDuration() || 8);
      }
    }, 500);
  }, [templateDuration]);

  const handleInitialPlay = useCallback(() => {
    setHasStarted(true);
    const preview = previewRef.current;
    if (preview) { preview.setTime(0); preview.play(); }
  }, []);

  const handlePlayPause = useCallback(() => {
    const preview = previewRef.current;
    if (!preview) return;
    if (isPlaying) preview.pause();
    else preview.play();
  }, [isPlaying]);

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!seekBarRef.current) return;
    const preview = previewRef.current;
    if (!preview) return;
    const rect = seekBarRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const newTime = ratio * duration;
    preview.setTime(newTime);
    setTimeImmediate(newTime);
  }, [duration, setTimeImmediate]);

  const onPlay = useCallback(() => setIsPlaying(true), []);
  const onPause = useCallback(() => setIsPlaying(false), []);

  // ===== Fake stepper state machine =====
  // currentStepIndex: the step that's currently in-progress. Steps before it are completed.
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Highest step index the stepper is allowed to reach given the current backend status.
  // We let the stepper sit on the LAST step of the current group while waiting for the
  // backend to transition; we never advance into a step whose group hasn't started yet.
  const maxAllowedStepIndex = useMemo(() => {
    if (status === 'completed') return STEPS.length - 1;
    const statusIdx = STATUS_ORDER.indexOf(status as StatusGroup);
    if (statusIdx < 0) return 0;
    let last = -1;
    for (let i = 0; i < STEPS.length; i++) {
      const sIdx = STATUS_ORDER.indexOf(STEPS[i]!.group);
      if (sIdx <= statusIdx) last = i;
    }
    return last >= 0 ? last : 0;
  }, [status]);

  // Snap forward when the backend overtakes us (e.g. user opens the chat mid-generation,
  // or a status transition lands while we're still on an earlier step).
  useEffect(() => {
    const firstStepOfStatus = STEPS.findIndex((s) => s.group === status);
    if (firstStepOfStatus > currentStepIndex) {
      setCurrentStepIndex(firstStepOfStatus);
    }
  }, [status]);

  // Auto-advance through steps using their per-step delays, but never past maxAllowedStepIndex.
  // When we're capped (waiting on backend), the timer simply doesn't fire.
  useEffect(() => {
    if (status === 'failed') return;
    if (currentStepIndex >= maxAllowedStepIndex) return;

    const nextStep = STEPS[currentStepIndex + 1];
    if (!nextStep) return;

    const timer = setTimeout(() => {
      setCurrentStepIndex((prev) => Math.min(prev + 1, maxAllowedStepIndex));
    }, nextStep.delayMs);

    return () => clearTimeout(timer);
  }, [currentStepIndex, status, maxAllowedStepIndex]);

  // Auto-correct duration if the SDK reports a larger playback time than the cached duration
  // (happens when getDuration() returns a partial value before all tracks finished loading).
  const handleTimeChange = useCallback((time: number) => {
    if (time > duration + 0.1) {
      setDuration(time);
    }
    onTimeChange(time);
  }, [duration, onTimeChange]);

  // During the initial render window (before the first poll returns), show a neutral
  // Fusion loading instead of starting the 12-step stepper from scratch. This avoids
  // the visual flash on page refresh where the stepper briefly animates before the
  // already-completed video player takes over.
  if (!firstFetchDone) {
    return (
      <Box w="100%" maxW="360px" borderRadius="xl" border="1px solid" borderColor="gray.200" bg="white" my={2} p={8}>
        <Flex direction="column" align="center" justify="center" minH="200px">
          <FusionLoading isLoading />
        </Flex>
      </Box>
    );
  }

  if (error) {
    return (
      <Box w="100%" maxW="360px" borderRadius="xl" border="1px solid" borderColor="red.200" bg="red.50" p={4} my={2}>
        <Flex align="center" gap={2} mb={2}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#E53E3E" strokeWidth="2"/>
            <path d="M12 8V12M12 16H12.01" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <Text fontSize="sm" fontWeight="semibold" color="red.700">Something went wrong</Text>
        </Flex>
        <Text fontSize="xs" color="red.700" mb={3}>
          We couldn't generate your video ad this time. This can happen occasionally — just try again and it should work.
        </Text>
        <Button
          size="sm"
          colorScheme="red"
          variant="solid"
          onClick={handleRetry}
          isLoading={isRetrying}
          loadingText="Restarting..."
          width="100%"
        >
          Try again
        </Button>
      </Box>
    );
  }

  if (status === 'completed' && normalizedTemplate) {
    return (
      <>
        <Box w="100%" maxW="360px" borderRadius="lg" overflow="hidden" bg="white" border="2px solid" borderColor="green.300" boxShadow="sm" my={2}>
          {/* Same structure as VideoSquareCreative */}
          <Box
            position="relative"
            w="100%"
            pb="100%"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <Box
              position="absolute" top={0} left={0} right={0} bottom={0}
              filter={previewReady && !hasStarted ? 'brightness(0.7)' : undefined}
            >
              <TemplatePreview
                ref={previewRef}
                key={videoAdId}
                template={normalizedTemplate}
                width="100%"
                height="100%"
                allowInteraction
                onReady={onPreviewReady}
                onTimeChange={handleTimeChange}
                onPlay={onPlay}
                onPause={onPause}
              />
            </Box>

            {/* Initial centered play button — same as VideoSquareCreative */}
            {previewReady && !hasStarted && (
              <Flex
                position="absolute" top={0} left={0} right={0} bottom={0}
                align="center" justify="center" zIndex={2} cursor="pointer"
                onClick={handleInitialPlay}
              >
                <Flex w="56px" h="56px" borderRadius="full" bg="blackAlpha.600" align="center" justify="center" boxShadow="lg">
                  <Box as={PlayIcon} color="white" fontSize="20px" ml="3px" />
                </Flex>
              </Flex>
            )}

            {/* Controls bar — same as VideoSquareCreative */}
            {hasStarted && isHovering && (
              <Flex
                position="absolute" bottom={0} left={0} right={0}
                bg="blackAlpha.700" px={3} py={2} align="center" gap={2} zIndex={2}
              >
                <Box as="button" onClick={handlePlayPause} color="white" cursor="pointer" flexShrink={0} _hover={{ opacity: 0.8 }}>
                  <Box as={isPlaying ? PauseIcon : PlayIcon} fontSize="14px" ml={isPlaying ? '0' : '2px'} />
                </Box>
                <Box ref={seekBarRef} flex={1} h="6px" bg="whiteAlpha.300" borderRadius="full" cursor="pointer" onClick={handleSeek} position="relative">
                  <Box h="100%" bg="white" borderRadius="full" ref={progressBarRef} w="0%" />
                </Box>
                <Text ref={timeDisplayRef} color="white" fontSize="xs" flexShrink={0} whiteSpace="nowrap">0:00 / 0:00</Text>
                <Box as="button" onClick={(e: React.MouseEvent) => { e.stopPropagation(); openZoom(); }} color="white" cursor="pointer" flexShrink={0} _hover={{ opacity: 0.8 }}>
                  <Box as={ExpandIcon} fontSize="12px" />
                </Box>
              </Flex>
            )}
          </Box>

          <Flex p={2} align="center" gap={2} bg="green.50" borderTop="1px solid" borderColor="green.200">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17L4 12" stroke="#38A169" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <Text fontSize="xs" fontWeight="semibold" color="green.700">Video ad ready!</Text>
          </Flex>
        </Box>

        {/* Zoom Modal */}
        <Modal isOpen={isZoomOpen} onClose={closeZoom} isCentered size="lg">
          <ModalOverlay bg="blackAlpha.800" zIndex={20000} />
          <ModalContent containerProps={{ zIndex: 20001 }} bg="white" borderRadius="lg" maxW="500px" overflow="hidden">
            <IconButton
              aria-label="Close" icon={<CloseIcon />}
              position="absolute" top={2} right={2} zIndex={10} size="sm"
              bg="blackAlpha.600" color="white" borderRadius="full" _hover={{ bg: 'blackAlpha.800' }}
              onClick={closeZoom}
            />
            <Box w="100%" pb="100%" position="relative" bg="black">
              <Box position="absolute" top={0} left={0} right={0} bottom={0}>
                <TemplatePreview
                  template={normalizedTemplate}
                  width="100%"
                  height="100%"
                  allowInteraction
                />
              </Box>
            </Box>
          </ModalContent>
        </Modal>
      </>
    );
  }

  return (
    <Box w="100%" maxW="360px" borderRadius="xl" border="1px solid" borderColor="gray.200" bg="white" overflow="hidden" my={2}>
      <Flex p={3} bg="gray.50" borderBottom="1px solid" borderColor="gray.200" align="center" gap={2}>
        <Spinner size="sm" color="orange.400" thickness="2px" />
        <Text fontSize="sm" fontWeight="semibold" color="gray.700">Generating your video ad</Text>
      </Flex>

      <Box px={4} py={3}>
        {STEPS.map((step, idx) => {
          const isCompleted = idx < currentStepIndex;
          const isInProgress = idx === currentStepIndex;
          return (
            <Flex key={step.id} align="center" gap={3} py="5px">
              {/* Left: step number — always visible */}
              <Flex
                w="22px"
                h="22px"
                borderRadius="full"
                align="center"
                justify="center"
                flexShrink={0}
                bg={isCompleted ? 'green.50' : isInProgress ? 'orange.50' : 'gray.50'}
                border="1px solid"
                borderColor={isCompleted ? 'green.300' : isInProgress ? 'orange.300' : 'gray.200'}
              >
                <Text
                  fontSize="10px"
                  fontWeight="bold"
                  lineHeight="1"
                  color={isCompleted ? 'green.600' : isInProgress ? 'orange.500' : 'gray.400'}
                >
                  {step.id}
                </Text>
              </Flex>

              {/* Middle: label — fills the row */}
              <Text
                flex="1"
                fontSize="xs"
                color={isCompleted ? 'gray.500' : isInProgress ? 'gray.800' : 'gray.400'}
                fontWeight={isInProgress ? 'semibold' : 'normal'}
                animation={isInProgress ? `${pulseAnimation} 2s ease-in-out infinite` : undefined}
              >
                {step.label}
              </Text>

              {/* Right: status indicator — tick when done, spinner when in progress, empty when pending */}
              <Flex w="18px" h="18px" flexShrink={0} align="center" justify="center">
                {isCompleted && (
                  <Flex w="16px" h="16px" borderRadius="full" bg="green.500" align="center" justify="center">
                    <CheckMark />
                  </Flex>
                )}
                {isInProgress && <Spinner size="xs" color="orange.400" thickness="2px" />}
              </Flex>
            </Flex>
          );
        })}
      </Box>

      <Flex bg="gray.50" p={3} align="center" gap={2} borderTop="1px solid" borderColor="gray.200">
        <Box w="8px" h="8px" borderRadius="full" bg="orange.400" animation={`${pulseAnimation} 1.5s ease-in-out infinite`} />
        <Text fontSize="xs" color="gray.600">This usually takes a couple of minutes</Text>
      </Flex>
    </Box>
  );
};

export default VideoAdGenerating;
